import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Button, Box, Typography, Stack } from '@mui/material';
import { FavoriteBorder, Favorite, ShoppingCart, Public, MailOutline, DateRange } from '@mui/icons-material';
import ActionInfoButton from 'components/ActionInfoButton';
import MessageBrandDialog from 'components/Dialog/MessageBrandDialog/MessageBrandDialog';
import useResponsive from 'hooks/useResponsive';
import useAuth from 'hooks/useAuth';
import useFollowing from 'hooks/useFollowing';
import { truncate, calculateLeadTimeText } from 'utils/functions';
import { GTMFollowBrand } from 'utils/gtm';
import CustomTooltip from 'components/CustomTooltip';

// Constants
const BUTTON_TEXT_VALUES = {
  FOLLOW: 'FOLLOW BRAND',
  FOLLOWING: 'FOLLOWING',
  MESSAGE: 'MESSAGE',
} as const;

type ButtonTextType = (typeof BUTTON_TEXT_VALUES)[keyof typeof BUTTON_TEXT_VALUES];
type ButtonVariantType = 'contained' | 'outlined';

// Types and Interfaces
interface BrandData {
  name: string;
  avatarImageUrl?: string;
  country?: string;
  minimumOrderValue?: number;
  reorderValue?: number;
  currencySymbol?: string;
  wpId: string;
  roundedAverageLeadTime?: number;
  shipsIn?: string;
}

interface BrandHeaderProps {
  brandData: BrandData;
  isMobile: boolean;
  buttonText: ButtonTextType;
  buttonVariant: ButtonVariantType;
  onFollowClick: () => void;
  onMessageClick: () => void;
  isAuthenticated: boolean;
  isFollowing: boolean;
}

interface BrandInfoSectionProps {
  brandData: BrandData;
  isMobile: boolean;
}

interface BrandButtonsProps {
  buttonText: ButtonTextType;
  buttonVariant: ButtonVariantType;
  onFollowClick: () => void;
  onMessageClick: () => void;
  isMobile: boolean;
  isFollowing: boolean;
}

// Styles
const styles = {
  container: (isMobile: boolean) => ({
    backgroundColor: '#F8FAFA',
    mx: 'auto',
    p: 1,
    boxShadow: '0px 1px 5px 0px #0000001F',
    borderRadius: '0.6rem',
    minWidth: isMobile ? '100%' : '80%',
  }),
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 0.5,
  },
  brandInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  avatar: {
    width: 42,
    height: 42,
  },
  brandName: (isMobile: boolean) => ({
    fontWeight: 'bold',
    fontSize: isMobile ? '1rem' : '1.5rem',
  }),
  brandInfoStack: (isMobile: boolean) => ({
    flexDirection: isMobile ? 'column' : ('row' as const),
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
  }),
  brandButtons: (isMobile: boolean) => ({
    flexDirection: 'row' as const,
    gap: 0.5,
    justifyContent: isMobile ? 'center' : 'flex-end',
    mt: isMobile ? 1.5 : 0,
  }),
  mobileButton: (isMobile: boolean) => ({
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
  }),
};

// Utility Functions
const formatMinimumReorderText = (brandData: BrandData): string | undefined => {
  const { minimumOrderValue, reorderValue, currencySymbol } = brandData;

  if (minimumOrderValue !== undefined && reorderValue !== undefined && currencySymbol) {
    return `Minimum ${currencySymbol}${minimumOrderValue} / Reorder ${currencySymbol}${truncate(reorderValue)}`;
  }
  return undefined;
};

const getLeadTimeText = (brandData: BrandData): string | undefined => {
  return calculateLeadTimeText({
    roundedAverageLeadTime: brandData?.roundedAverageLeadTime,
    shipsIn: brandData?.shipsIn,
    bold: false,
  });
};

// Sub-components
const BrandButtons: React.FC<BrandButtonsProps> = ({
  buttonText,
  buttonVariant,
  onFollowClick,
  onMessageClick,
  isMobile,
  isFollowing,
}) => (
  <Stack sx={styles.brandButtons(isMobile)}>
    <Button
      variant={buttonVariant}
      startIcon={isFollowing ? <Favorite /> : <FavoriteBorder />}
      data-testid="brand-information-container-leftInformation-follow-button"
      color="primary"
      onClick={onFollowClick}
      sx={styles.mobileButton(true)}
    >
      {buttonText}
    </Button>

    <CustomTooltip
      title={
        <Typography>
          We’re sorry, messaging is temporarily unavailable while we work on improvements. If you’ve already placed an
          order with this seller, you can still message them from your <strong>Dashboard.</strong> Thanks for your
          patience!
        </Typography>
      }
    >
      <Button
        variant="outlined"
        startIcon={<MailOutline />}
        data-testid="brand-information-container-leftInformation-message-button"
        onClick={onMessageClick}
        sx={styles.mobileButton(true)}
      >
        {BUTTON_TEXT_VALUES.MESSAGE}
      </Button>
    </CustomTooltip>
  </Stack>
);

const BrandHeader: React.FC<BrandHeaderProps> = ({
  brandData,
  isMobile,
  buttonText,
  buttonVariant,
  onFollowClick,
  onMessageClick,
  isAuthenticated,
  isFollowing,
}) => (
  <Box sx={styles.header}>
    <Box sx={styles.brandInfo}>
      <Avatar
        sx={styles.avatar}
        aria-label={brandData.name}
        src={brandData.avatarImageUrl}
        alt={`${brandData.name} avatar`}
      />
      <Typography variant="h5" sx={styles.brandName(isMobile)} className="notranslate">
        {brandData.name}
      </Typography>
    </Box>
    {!isMobile && isAuthenticated && (
      <BrandButtons
        buttonText={buttonText}
        buttonVariant={buttonVariant}
        onFollowClick={onFollowClick}
        onMessageClick={onMessageClick}
        isMobile={isMobile}
        isFollowing={isFollowing}
      />
    )}
  </Box>
);

const BrandInfoSection: React.FC<BrandInfoSectionProps> = ({ brandData, isMobile }) => {
  const minimumReorderText = useMemo(() => formatMinimumReorderText(brandData), [brandData]);
  const leadTimeText = useMemo(() => getLeadTimeText(brandData), [brandData]);

  return (
    <Stack sx={styles.brandInfoStack(isMobile)}>
      <ActionInfoButton
        Icon={ShoppingCart}
        message={minimumReorderText}
        dataTestId="brand-information-container-leftInformation-minimum"
      />
      <ActionInfoButton
        Icon={Public}
        message={brandData.country}
        dataTestId="brand-information-container-leftInformation-country"
      />
      <ActionInfoButton
        Icon={DateRange}
        message={leadTimeText}
        dataTestId="brand-information-container-leftInformation-leadtime"
      />
    </Stack>
  );
};

// Main Component
const BrandInformationCard: React.FC<{ brandData: BrandData }> = ({ brandData }) => {
  const isMobile = useResponsive('down', 'md');
  const { userId, isAuthenticated } = useAuth();
  const { following, follow, unfollow } = useFollowing();

  // State management
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<ButtonTextType>(BUTTON_TEXT_VALUES.FOLLOW);
  const [buttonVariant, setButtonVariant] = useState<ButtonVariantType>('contained');
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  // Check if user is following this brand
  useEffect(() => {
    if (following && brandData.wpId) {
      setIsFollowing(following.includes(brandData.wpId));
    }
  }, [following, brandData.wpId]);

  // Update button text and variant based on following state
  useEffect(() => {
    if (isFollowing) {
      setButtonText(BUTTON_TEXT_VALUES.FOLLOWING);
      setButtonVariant('outlined');
    } else {
      setButtonText(BUTTON_TEXT_VALUES.FOLLOW);
      setButtonVariant('contained');
    }
  }, [isFollowing]);

  // Event handlers
  const handleFollowButtonClick = (): void => {
    if (isFollowing) {
      unfollow(brandData.wpId);
    } else {
      follow(brandData.wpId);
      GTMFollowBrand(brandData.name, brandData.wpId, brandData.country, brandData.shipsIn, userId);
    }
  };

  const handleMessageButtonClick = (): void => {
    setMessageDialogOpen(true);
  };

  const handleMessageDialogClose = (): void => {
    setMessageDialogOpen(false);
  };

  return (
    <Box sx={styles.container(isMobile)}>
      <BrandHeader
        brandData={brandData}
        isMobile={isMobile}
        buttonText={buttonText}
        buttonVariant={buttonVariant}
        onFollowClick={handleFollowButtonClick}
        onMessageClick={handleMessageButtonClick}
        isAuthenticated={isAuthenticated}
        isFollowing={isFollowing}
      />

      <BrandInfoSection brandData={brandData} isMobile={isMobile} />

      {isMobile && isAuthenticated && (
        <BrandButtons
          buttonText={buttonText}
          buttonVariant={buttonVariant}
          onFollowClick={handleFollowButtonClick}
          onMessageClick={handleMessageButtonClick}
          isMobile={isMobile}
          isFollowing={isFollowing}
        />
      )}

      <MessageBrandDialog
        brandData={{
          name: brandData.name,
          wpId: brandData.wpId,
        }}
        messageDialogOpen={messageDialogOpen}
        handleMessageDialogClose={handleMessageDialogClose}
      />
    </Box>
  );
};

export default BrandInformationCard;
